import { notify } from "@general-backoffice/core/index";
import { useEffect, useRef, useState } from "react";

// import { useSelector } from "react-redux";
import { type BillingData, billingDataEndpoints } from "../../../api";
import { type StrapiId, useStrapiOne } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import { getFromCache, setToCache } from "../../../utils/memoryCache";
import handleError from "../../utils/handleError";

const CACHE_KEY = "my_billing_data";
const ISSUER_CACHE_KEY = "issuer_billing_data";

interface UseMyBillingDataProps {
  [key: string]: unknown;
}

const useMyBillingData = (autoload: StrapiId | undefined, props?: UseMyBillingDataProps, issuer?: boolean) => {
  const cacheKey = issuer ? ISSUER_CACHE_KEY : CACHE_KEY;
  const cached = getFromCache<BillingData>(cacheKey);
  const [cachedData, setCachedData] = useState(cached?.data || null);
  const hasInitialCache = useRef(!!cached);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // const { user } = useSelector(({ sessionState }) => sessionState)
  const {
    item: billingData,
    setItem: setBillingData,
    isLoading,
    setLoading,
    ...other // isLoading, setLoading, load, update, remove
  } = useStrapiOne({
    id: autoload,
    ...props,
    loadItem: issuer ? loadMyIssuerBillingData : loadMyBillingData,
    submitItem: issuer ? submitMyIssuerBillingData : submitMyBillingData,
  });

  // Update cache when fresh data arrives
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!isLoading && billingData) {
      setToCache(cacheKey, billingData);
      setCachedData(billingData);
    }
  }, [billingData, isLoading, cacheKey]);

  // Use cached data while loading, fresh data when available
  const effectiveData = billingData || cachedData;
  const effectiveLoading = hasInitialCache.current ? false : isLoading;

  return {
    billingData: effectiveData,
    setBillingData,
    isLoading: effectiveLoading,
    isLoadingFresh: isLoading,
    setLoading,
    ...other,
  };
};

export const loadMyBillingData = async (): Promise<BillingData | undefined> => {
  try {
    const { data } = await billingDataEndpoints.getMyBillingData();
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Error while loading billing data"));
  }
};
export const loadMyIssuerBillingData = async (): Promise<BillingData | undefined> => {
  try {
    const { data } = await billingDataEndpoints.getMyIssuerBillingData();
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Error while loading billing data"));
  }
};

export const submitMyBillingData = async (billingData: BillingData): Promise<BillingData | undefined> => {
  const errMsg = "Billing data update failed";
  if (!billingData) throw new Error(errMsg);

  try {
    const { data } = await billingDataEndpoints.updateMyBillingData(billingData);
    notify.success("Billing data update finished correctly");
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, errMsg));
  }
};
export const submitMyIssuerBillingData = async (billingData: BillingData): Promise<BillingData | undefined> => {
  const errMsg = "Billing data update failed";
  if (!billingData) throw new Error(errMsg);

  try {
    const { data } = await billingDataEndpoints.updateMyIssuerBillingData(billingData);
    notify.success("Billing data update finished correctly");
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, errMsg));
  }
};

export default useMyBillingData;
